import React, { useCallback, useEffect, useRef, useState } from "react";
import masterCvPdf from "../../assets/Liz_Osborn_Master_CV.pdf";
import resumePdf from "../../assets/Liz_Osborn_Resume.pdf";
import portrait from "../../assets/img/liz-portrait-plate.jpg";
import { REV } from "../plate/SheetFurniture";
import SheetNote from "../SheetNote";

const ExperienceHeader = ({ title, dates, sub }) => (
  <>
    <h4 className="cv-subhead">
      {title}
      {dates && <span className="cv-subhead__dates"> · {dates}</span>}
    </h4>
    {sub && <p className="cv-exp-sub">{sub}</p>}
  </>
);

const Achievements = ({ items }) => (
  <>
    {items.map((it, i) => (
      <div key={i} className="cv-card">
        {it}
      </div>
    ))}
  </>
);

const AwardEntry = ({ title, dates }) => (
  <div className="cv-award">
    <span>{title}</span>
    <span className="cv-award__dates">{dates}</span>
  </div>
);

// What used to be a tab is now a native disclosure. <details> is keyboard
// operable and announced by assistive tech with no ARIA, and its content stays
// in the DOM, so find-in-page and screen readers still reach every entry. The
// heading lives inside <summary> so the outline (h2 > h3) is unchanged and
// heading navigation still lists every group.
const SyncContext = React.createContext(() => {});

const Group = ({ title, meta, children }) => {
  const sync = React.useContext(SyncContext);
  return (
    <details className="cv-group" onToggle={sync}>
      <summary className="cv-group__summary">
        <h3 className="cv-group__title">{title}</h3>
        {meta && <span className="cv-group__meta">{meta}</span>}
      </summary>
      <div className="cv-group__body">{children}</div>
    </details>
  );
};

const Section = ({ id, title, sub, children }) => (
  <section id={id} className="cv-section" aria-labelledby={`${id}-heading`}>
    <h2 className="cv-section__title" id={`${id}-heading`}>{title}</h2>
    {sub && <p className="cv-section__sub">{sub}</p>}
    {children}
  </section>
);

// Decorative ruled-border zone marks, as on a drawing sheet.
const ZONES_X = ["1", "2", "3", "4", "5", "6"];
const ZONES_Y = ["A", "B", "C", "D", "E", "F"];

const CurriculumVitae = () => {
  const root = useRef(null);
  const [allOpen, setAllOpen] = useState(false);
  const all = () => Array.from(root.current ? root.current.querySelectorAll("details.cv-group") : []);

  const sync = useCallback(() => {
    const d = Array.from(root.current ? root.current.querySelectorAll("details.cv-group") : []);
    setAllOpen(d.length > 0 && d.every((x) => x.open));
  }, []);

  const toggleAll = () => {
    const next = !allOpen;
    all().forEach((d) => { d.open = next; });
    setAllOpen(next);
  };

  // A printed or saved-as-PDF CV must show everything: open all groups for the
  // print, then put back whatever the reader had open.
  useEffect(() => {
    let saved = null;
    const before = () => {
      saved = all().map((d) => [d, d.open]);
      saved.forEach(([d]) => { d.open = true; });
    };
    const after = () => {
      if (saved) saved.forEach(([d, o]) => { d.open = o; });
      saved = null;
      sync();
    };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, [sync]);

  return (
    <SyncContext.Provider value={sync}>
    <main className="cv-sheet" id="main" ref={root}>
      <div className="cv-sheet__zones cv-sheet__zones--x" aria-hidden="true">
        {ZONES_X.map((z) => <span key={z}>{z}</span>)}
      </div>
      <div className="cv-sheet__zones cv-sheet__zones--y" aria-hidden="true">
        {ZONES_Y.map((z) => <span key={z}>{z}</span>)}
      </div>

      <header className="cv-head">
        <img
          className="cv-head__portrait"
          src={portrait}
          alt="Liz Osborn, drawn as an engineering blueprint: smiling, hair back, wearing a black turtleneck under an open jacket patterned with orange and slate leaves. The sheet around her carries zone letters, dimension arrows and two callouts — Detail A, the jacket motif, and Detail B, a node-and-edge system diagram. A title block at the lower right reads LIZ OSBORN, ENGINEER."
        />
        <div className="cv-head__text">
          <p className="cv-head__eyebrow">CURRICULUM VITAE</p>
          <h1 className="cv-head__name">Liz Osborn</h1>
          <p className="cv-head__role">
            Industrial Engineering @ Cal Poly Pomona — Graduating 2027
          </p>
          <div className="cv-head__actions">
            <a className="cv-btn" href={masterCvPdf} download="Liz_Osborn_Master_CV.pdf">
              Download CV
            </a>
            <a className="cv-btn" href={resumePdf} download="Liz_Osborn_Resume.pdf">
              Download Resume
            </a>
          </div>
        </div>
      </header>

      <div className="cv-controls">
        <button type="button" className="cv-expand" onClick={toggleAll}>
          {allOpen ? "Collapse all" : "Expand all"}
        </button>
      </div>

      <Section id="publications" title="Publications & Presentations">
        <>
          <Group title="Published">
            <div className="pt-4">
              <h4 className="cv-subhead">Peer-Reviewed Conference Papers</h4>
              <div className="cv-card">
                Osborn, E., &amp; Woo, J. (2025, April). Engineering Pathways in
                Education and The Credit Loss Challenge. Paper presented at the
                2025 ASEE Pacific Southwest Conference, California Polytechnic
                University, California.{" "}
                <a
                  href="https://peer.asee.org/engineering-pathways-in-education-and-the-credit-loss-challenge"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  DOI: 10.18260/1-2--55171
                </a>
              </div>

              <h4 className="cv-subhead">Research Artifacts</h4>
              <div className="cv-card">
                Osborn, E. (2026). Interface-Anchored Knowledge Mapping: A
                Coordinate System for Locating Knowledge-Relevant Metadata in
                Modular Sociotechnical Systems. Zenodo.{" "}
                <a
                  href="https://doi.org/10.5281/zenodo.22668058"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  DOI: 10.5281/zenodo.22668058
                </a>
              </div>
              <div className="cv-card">
                Osborn, E. (2026). Open Source CubeSat Library Database Schema
                for the Interface-Anchored Knowledge Mapping System. Zenodo.{" "}
                <a
                  href="https://doi.org/10.5281/zenodo.19780470"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  DOI: 10.5281/zenodo.19780470
                </a>
              </div>
            </div>
          </Group>
          <Group title="Patents">
            <div className="pt-4">
              <h4 className="cv-subhead">Provisional</h4>
              <div className="cv-card">
                Osborn, E.{" "}
                <em>
                  Interface-Anchored Knowledge Mapping System and Method.
                </em>{" "}
                Self-filed U.S. Provisional Patent Application, April 2026.
              </div>
            </div>
          </Group>
          <Group title="Presentations">
            <div className="pt-4">
              <h4 className="cv-subhead">Oral Presentations</h4>
              <div className="cv-card">
                Osborn, E., &amp; Woo, J. (2025, April). Engineering Pathways
                in Education and The Credit Loss Challenge. Oral presentation
                at the ASEE Pacific Southwest Regional Conference, California
                State Polytechnic University, Pomona.
              </div>

              <h4 className="cv-subhead">Poster Presentations</h4>
              <div className="cv-card">
                Osborn, E. (2025, October). FRAMES: A Structural Diagnostic for
                Resilience in Modular University Space Programs. Poster
                presented at the 2025 NSF S-STEM Scholars Meeting, San Diego,
                CA.
              </div>
              <div className="cv-card">
                Osborn, E. (2025, September). FRAMES: A Structural Diagnostic
                for Resilience in Modular University Space Programs. Poster
                presented at the STEM Research Symposium, Citrus College,
                Glendora, CA.
              </div>
              <div className="cv-card">
                Osborn, E. (2025, August). FRAMES: A Structural Diagnostic for
                Resilience in Modular University Space Programs (Framework for
                Resilience Assessment in Modular Engineering Systems). Poster
                presented at the 11th Annual CARS, California State Polytechnic
                University, Pomona.{" "}
                <a
                  href="https://badges.parchment.com/public/assertions/y169O6pnTcGZaoxfjO2aTg?identity__email=eosborn%40cpp.edu"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Verified credential
                </a>
              </div>
            </div>
          </Group>
        </>
      </Section>

      <Section id="software" title="Software">
        <>
            <Group title="Knowledge Observatory">
              <div className="cv-card">
                <p className="cv-software__desc">Open-source implementation of the Interface-Anchored Knowledge Mapping (IAKM) system.</p>
              <p className="cv-software__meta">
                Apache-2.0 ·{" "}
                <a href="https://github.com/Lizo-RoadTown/PROVES_LIBRARY" target="_blank" rel="noreferrer noopener">
                  github.com/Lizo-RoadTown/PROVES_LIBRARY
                </a>
              </p>
              </div>
            </Group>
            <Group title="Tapestry">
              <div className="cv-card">
                <p className="cv-software__desc">A user/agent support and reinforcement system.</p>
              <p className="cv-software__meta">
                Apache-2.0 ·{" "}
                <a href="https://github.com/Lizo-RoadTown/tapestry" target="_blank" rel="noreferrer noopener">
                  github.com/Lizo-RoadTown/tapestry
                </a>
              </p>
              </div>
            </Group>
            <Group title="tapestry-cli" meta="v0.1.3">
              <div className="cv-card">
                <p className="cv-software__desc">Command-line interface for Tapestry.</p>
              <p className="cv-software__meta">
                Python Package Index ·{" "}
                <a href="https://pypi.org/project/tapestry-cli/" target="_blank" rel="noreferrer noopener">
                  pypi.org/project/tapestry-cli
                </a>
              </p>
              </div>
            </Group>
            <Group title="tapestry-discipline" meta="v0.1.16">
              <div className="cv-card">
                <p className="cv-software__desc">Session-lifecycle discipline hooks for agentic development.</p>
              <p className="cv-software__meta">
                Claude Code plugin · Tapestry Marketplace ·{" "}
                <a href="https://github.com/Lizo-RoadTown/tapestry" target="_blank" rel="noreferrer noopener">
                  github.com/Lizo-RoadTown/tapestry
                </a>
              </p>
              </div>
            </Group>
            <Group title="tapestry-patterns" meta="v0.1.2">
              <div className="cv-card">
                <p className="cv-software__desc">Canonical patterns library for agentic systems.</p>
              <p className="cv-software__meta">
                Claude Code plugin · Tapestry Marketplace ·{" "}
                <a href="https://github.com/Lizo-RoadTown/tapestry" target="_blank" rel="noreferrer noopener">
                  github.com/Lizo-RoadTown/tapestry
                </a>
              </p>
              </div>
            </Group>
        </>
      </Section>

      <Section id="research" title="Research Experience">
        <>
          <Group title="Systems Medicine">
            <div className="pt-4">
              <ExperienceHeader
                title="University of Florida Systems Medicine Student Researcher"
                dates="March 2026 – June 2026"
                sub="Supervisor: Dr. T.J. Sego"
              />
              <Achievements
                items={[
                  "Evaluates stochastic differential equation models and documents reproducibility barriers and findings.",
                  "Implements models in Python (JAX, diffrax) to attempt figure reproduction, resolving parameter conflicts and missing variable definitions in the process.",
                  "Coordinates a 4-person team and leads working sessions on the program's GitHub-based reproducibility-review infrastructure.",
                ]}
              />
            </div>
          </Group>
          <Group title="Aerospace">
            <div className="pt-4">
              <ExperienceHeader
                title="Bronco Space Lab Student Researcher"
                dates="June 2025 – March 2026"
                sub="Supervisor: Michael Pham"
              />
              <Achievements
                items={[
                  "Designed the Interface-Anchored Knowledge Mapping System (IAKM); self-filed provisional patent, April 2026.",
                  "Published the system's open-source CubeSat database schema as a citable artifact on Zenodo (DOI: 10.5281/zenodo.19780470).",
                  "Developed IAKM through a six-attempt iterative design process using hierarchical task analysis and constraint-based evaluation.",
                  "Deployed the prototype across five university CubeSat programs (CPP, Columbia, Northeastern, UC Santa Cruz, Texas State).",
                  "Built the agentic extraction-and-verification application (Deep Agents pipeline, bifurcated storage, two-pass verification gate, live 3D knowledge graph).",
                ]}
              />
            </div>
          </Group>
          <Group title="Micro & Nanotechnology">
            <div className="pt-4">
              <ExperienceHeader
                title="Microgravity &amp; Nanotechnology Student Researcher"
                dates="July 2025 – February 2026"
                sub="Supervisors: Dr. Jared Ashcroft & Michael Pham"
              />
              <Achievements
                items={[
                  "Completed training in nanofabrication manufacturing and technology through virtual simulation modules.",
                  "Led an 8-member interdisciplinary team through full concept development, experimental design, and proposal authorship for an original microgravity biosensor experiment.",
                  "Designed and authored an original microgravity biosensor experiment proposal for SSEP Mission 21 — competitively reviewed at the national level and recognized with Honorable Mention.",
                ]}
              />
            </div>
          </Group>
          <Group title="Engineering Education">
            <div className="pt-4">
              <ExperienceHeader
                title="Engineering Education Lead Student Researcher"
                dates="September 2024 – May 2025"
                sub="Supervisor: Dr. Jeyoung Woo"
              />
              <Achievements
                items={[
                  "Served as Lead Student Researcher on a multi-state engineering education pathways study examining systemic credit transfer breakdowns across universities nationally.",
                  "Synthesized pathways data across four states and developed comparative frameworks identifying credit transfer process failures.",
                  "Produced evidence-based policy recommendations impacting degree transfer processes across all CSU campuses.",
                  "Authored and presented findings as first author at the ASEE Pacific Southwest Conference 2025; paper published in conference proceedings — doi:10.18260/1-2--55171.",
                ]}
              />
            </div>
          </Group>
        </>
      </Section>

      <Section id="professional" title="Professional Experience">
        <>
          <Group title="Business & Operations">
            <div className="pt-4">
              <ExperienceHeader
                title="Founder &amp; Managing Director"
                dates="10 years"
                sub="Boulder Punch · Pomona, CA"
              />
              <Achievements
                items={[
                  "Founded and operated an independent global retail business — identified unmet consumer demand in international markets and built the supply chain and distribution infrastructure to serve it across Amazon, eBay, and proprietary storefronts.",
                  "Managed synchronized multi-channel retail infrastructure for 2,000+ SKUs, optimizing seasonal demand routing across warehouse locations.",
                  "Iterated warehouse operations — floor layout, rack mapping, inventory systems, and fulfillment workflows — to fit changing business needs.",
                  "Managed developers building proprietary software serving buyers and vendors; designed system architecture and requirements independently.",
                  "Engaged as a consultant — replicated business model, onboarded teams, and connected inventory systems.",
                ]}
              />
            </div>
          </Group>
          <Group title="Education Outreach">
            <div className="pt-4">
              <ExperienceHeader
                title="CTE Outreach &amp; Events Assistant"
                dates="1 year"
                sub="Citrus College, Career and Technical Education · Glendora, CA"
              />
              <Achievements
                items={[
                  "Conducted CTE outreach at local high schools, elementary schools, and on-campus Handshake events — represented programs and onboarded students to the platform.",
                  "Produced student-facing communications — redeveloped the office's printed literature and pamphlets and created Canva welcome and orientation videos for incoming high school and college students.",
                  "Handled public inquiries routed from the state and supported program operations, including setup and takedown for career-readiness events.",
                ]}
              />
            </div>
          </Group>
          <Group title="Judicial Systems">
            <div className="pt-4">
              <ExperienceHeader
                title="Judicial Assistant to Superior Court Judges"
                dates="4 years"
                sub="Superior Court of California, County of San Bernardino · Supervisor: Ebony Dixie"
              />
              <Achievements
                items={[
                  "Worked directly with developers on county-wide court management system transition, covering criminal, juvenile, family, and civil law across warehouse, courtroom, and office procedures.",
                  "Assessed system impact on existing workflows and developed efficient procedural frameworks that remain in use today.",
                  "Led local court teams in weekly meetings and reported findings directly to the Court Executive Officer.",
                  "Managed official court records and courtroom operations for Superior Court judges presiding over family law and criminal court proceedings.",
                  "Served as clerk to the Honorable Judge Uhler for San Bernardino County's Homeless Court proceedings — experience at the intersection of the legal system and social services infrastructure.",
                ]}
              />
            </div>
          </Group>
          <Group title="Database Development">
            <div className="pt-4">
              <ExperienceHeader
                title="Contract Database Developer"
                dates="1 year"
                sub="Snelling Temporary Services"
              />
              <h4 className="cv-subhead">Gilead Sciences</h4>
              <Achievements
                items={[
                  "Built a Microsoft Access database storing microbiology lab results to meet OSHA compliance requirements.",
                ]}
              />
              <h4 className="cv-subhead">Claremont McKenna College, Gifts Office</h4>
              <Achievements
                items={[
                  "Built the alumni database for the gifts office in Microsoft Access.",
                ]}
              />
            </div>
          </Group>
        </>
      </Section>

      <Section id="teaching" title="Teaching & Mentoring">
        <>
          <Group title="Homework House">
            <div className="pt-4">
              <ExperienceHeader
                title="Mathematics Mentor"
                dates="September 2024 – December 2024"
                sub="Covina, CA"
              />
              <Achievements
                items={[
                  "Provided individualized mathematics mentoring to K-12 students; identified conceptual gaps and adapted explanations to each student's preparation level.",
                ]}
              />
            </div>
          </Group>
          <Group title="TRIO STEM">
            <div className="pt-4">
              <ExperienceHeader
                title="TRIO STEM Professional Expert"
                dates="June 2026 – Present"
                sub="Citrus College, TRIO STEM · Glendora, CA · Supervisor: My Chau"
              />
              <Achievements
                items={[
                  "Deliver AI workshops and tutor students in calculus-based mathematics, engineering, and programming; help students translate existing skills into technical learning pathways.",
                ]}
              />
            </div>
          </Group>
        </>
      </Section>

      <Section id="training" title="Training & Certifications">
        <>
          <Group title="NASA">
          <div className="cv-card">
            <p className="cv-cred__name">
              AI/ML in Space Biology
              <span className="cv-cred__date">September 2026</span>
            </p>
            Regression, classification, and explainable-AI notebooks on NASA
            OSDR RNA-seq data (scikit-learn, SHAP, PyDESeq2, GSEApy, Scanpy).
          </div>
          <div className="cv-card">
            <p className="cv-cred__name cv-cred__name--only">
              GeneLab for Colleges and Universities (GL4U) 2026
              <span className="cv-cred__date">October 2026</span>
            </p>
          </div>

          </Group>
          <Group title="NASA Jet Propulsion Laboratory">
          <div className="cv-card">
            <p className="cv-cred__name">
              Advanced F&prime; Flight Software Workshop
              <span className="cv-cred__date">February 2026</span>
            </p>
            One-week, hands-on workshop in the F&prime; flight-software
            framework: component architecture, commands, telemetry, and
            flight-software development.
          </div>

          </Group>
          <Group title="KC7 Foundation / Microsoft GHOST Team">
          <div className="cv-card">
            <p className="cv-cred__name">
              KC7 Cybersecurity Investigation Workshop
              <span className="cv-cred__date">March 2025</span>
            </p>
            One-day, hands-on cyber-investigation workshop using KQL and
            security-event data to reconstruct simulated malicious activity.
          </div>

          </Group>
          <Group title="Cal Poly Pomona">
          <div className="cv-card">
            <p className="cv-cred__name">
              CITI Program: Human Subjects Research, Basic Course
              <span className="cv-cred__date">February 2026</span>
            </p>
            Valid through February 2031.
          </div>
          </Group>
        </>
      </Section>

      <Section id="skills" title="Technical Skills">
        <>
          <Group title="Programming &amp; Data">
            <ul className="cv-skills__items">
              <li>Python (JAX, diffrax, NumPy, Pandas)</li>
              <li>SQL (PostgreSQL)</li>
              <li>Python packaging and distribution</li>
              <li>Model Context Protocol servers</li>
              <li>Claude plugin and hook development</li>
            </ul>
          </Group>

          <Group title="AI &amp; Agentic Systems">
            <ul className="cv-skills__items">
              <li>Agent orchestration (deep agents, LangGraph, recursive)</li>
              <li>Ontology design and ontological contracts</li>
              <li>Knowledge graph construction</li>
              <li>Metadata architecture</li>
              <li>Human-in-the-loop validation</li>
              <li>Pipeline observability (LangSmith, OpenTelemetry, Grafana)</li>
            </ul>
          </Group>

          <Group title="ML &amp; Modeling">
            <ul className="cv-skills__items">
              <li>Classification, clustering, and regression</li>
              <li>
                Predictive modeling in biological, environmental, and
                epidemiological data
              </li>
            </ul>
          </Group>

          <Group title="Data Governance &amp; Provenance">
            <ul className="cv-skills__items">
              <li>Data lineage</li>
              <li>Hash-linked provenance</li>
              <li>Audit trails</li>
              <li>DOI-versioned snapshots</li>
              <li>Version-controlled system states</li>
              <li>Reproducibility controls</li>
            </ul>
          </Group>

          <Group title="Systems Engineering">
            <ul className="cv-skills__items">
              <li>Hierarchical task analysis</li>
              <li>Process, interface, and information-flow modeling</li>
              <li>State and sequence diagramming</li>
              <li>Decision-gate analysis</li>
              <li>Root cause analysis</li>
              <li>Risk and resilience reasoning</li>
            </ul>
          </Group>

          <Group title="Research Methods">
            <ul className="cv-skills__items">
              <li>Experiment design</li>
              <li>Stochastic differential equation modeling</li>
              <li>Human-factors evaluation design</li>
              <li>IRB human subjects research protocols</li>
            </ul>
          </Group>
        </>
      </Section>

      <Section id="honors" title="Honors, Service & Memberships">
        <>
<Group title="Honors & Awards">
            <div className="pt-4">
              <h4 className="cv-subhead">NSF AT3 Scholar</h4>
              <div className="cv-card">
                <AwardEntry
                  title="Micro Nano Technology Education Center (MNT-EC)"
                  dates="2025 / 2026"
                />
              </div>

              <h4 className="cv-subhead">National Scholar</h4>
              <div className="cv-card">
                S-STEM Scholar — NSF, Citrus College; nominated to present at the NSF S-STEM National Meeting.
              </div>

              <h4 className="cv-subhead">NSF Pathways to STEM Scholar</h4>
              <div className="cv-card">
                <AwardEntry
                  title="National Science Foundation, Oakcrest Institute of Science"
                  dates="2024 / 2025"
                />
              </div>

              <h4 className="cv-subhead">Edison STEM Scholar</h4>
              <div className="cv-card">
                <AwardEntry
                  title="Edison STEM Scholar Scholarship, Edison International"
                  dates="2024 / 2025 / 2026 / 2027"
                />
              </div>

              <h4 className="cv-subhead">Honorable Mention</h4>
              <div className="cv-card">
                <a
                  href="http://ssep.ncesse.org/communities/experiments-selected-for-flight/selected-experiments-on-ssep-mission-21-to-iss/#:~:text=California%20State%20Polytechnic%20University%2C%20Pomona"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  NCESSE SSEP Mission 21
                </a>
                — experiment design reviewed for flight to the ISS, selected from a national cohort.
              </div>
            </div>
          </Group>
<Group title="Service">
            <div className="pt-4">
              <h4 className="cv-subhead">Paper Reviewer</h4>
              <div className="cv-card">
                <AwardEntry
                  title="ASEE Graduate Division (student member)"
                  dates="2026 Annual Conference"
                />
              </div>

              <h4 className="cv-subhead">AI Prototyper</h4>
              <div className="cv-card">
                <AwardEntry
                  title="NASA International Space Apps Challenge"
                  dates="October 2026"
                />
              </div>
            </div>
          </Group>
<Group title="Memberships">
            <div className="pt-4">
              <div className="cv-card">
                <AwardEntry
                  title="NASA Open Science Data Repository (OSDR) Analysis Working Group"
                  dates="July 2026 – Present"
                />
              </div>
              <div className="cv-card">
                <AwardEntry title="Society of Women Engineers (SWE)" dates="2025 – Present" />
              </div>
              <div className="cv-card">
                <AwardEntry title="Institute of Industrial and Systems Engineers (IISE)" dates="2025 – Present" />
              </div>
              <div className="cv-card">
                <AwardEntry title="American Association for the Advancement of Science (AAAS)" dates="2025 – Present" />
              </div>
              <div className="cv-card">
                <AwardEntry title="Women in Manufacturing (WiM)" dates="2025 – Present" />
              </div>
              <div className="cv-card">
                <AwardEntry title="American Society for Engineering Education (ASEE)" dates="2024 – Present" />
              </div>
              <div className="cv-card">
                <AwardEntry title="Association for Computing Machinery (ACM)" dates="2025 – Present" />
              </div>
              <div className="cv-card">
                <AwardEntry title="Institute of Electrical and Electronics Engineers (IEEE)" dates="2025 – Present" />
              </div>
            </div>
          </Group>
        </>
      </Section>

      <SheetNote />

      <section className="cv-titleblock" aria-label="Title block">
        <p className="cv-titleblock__name">LIZ OSBORN</p>
        <p className="cv-titleblock__sub">CURRICULUM VITAE</p>
        <p className="cv-titleblock__cell">SHEET &nbsp;<b>C-1</b></p>
        <p className="cv-titleblock__cell">REV &nbsp;<b>{REV}</b></p>
      </section>
    </main>
    </SyncContext.Provider>
  );
};

export default CurriculumVitae;
